/**
 * Release Run Git Range
 *
 * @module features/releases/components/ReleaseRunGitRange
 * @description The git range for a release run — one compare link per repo
 * for multi-repo runs, a single link for legacy single-repo runs. Extracted
 * from the old `ReleaseRunDetail` page verbatim for reuse in `ReleaseRunPaneHeader`.
 */

import { Flex, Text } from '@chatsdet/ui';
import type { ReleaseRunDetail } from '../releaseTypes';

export function ReleaseRunGitRange({ run }: { run: ReleaseRunDetail }) {
  if (run.repoCheckpoints && run.repoCheckpoints.length > 1) {
    return (
      <Flex direction="column" gap="xs">
        {run.repoCheckpoints.map((cp) => {
          const repoLabel = cp.repoFullName.split('/').pop() ?? cp.repoFullName;
          if (cp.fromSha === null) {
            return (
              <Text key={cp.propertyGithubRepoId} size="sm" className="font-mono">
                {repoLabel}: ---..{cp.toSha.slice(0, 7)}
              </Text>
            );
          }
          const sameSha = cp.fromSha === cp.toSha;
          return (
            <a
              key={cp.propertyGithubRepoId}
              href={`https://github.com/${cp.repoFullName}/compare/${cp.fromSha}...${cp.toSha}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-mono text-primary hover:underline"
              title={cp.repoFullName}
            >
              {repoLabel}: {sameSha ? '(no changes)' : `${cp.fromSha.slice(0, 7)}..${cp.toSha.slice(0, 7)}`}
            </a>
          );
        })}
      </Flex>
    );
  }

  if (run.fromSha && run.toSha && run.repoFullName) {
    return (
      <a
        href={`https://github.com/${run.repoFullName}/compare/${run.fromSha}...${run.toSha}`}
        target="_blank"
        rel="noreferrer"
        className="text-sm font-mono text-primary hover:underline"
      >
        {run.fromSha.slice(0, 7)}..{run.toSha.slice(0, 7)}
      </a>
    );
  }

  return (
    <Text size="sm" className="font-mono">
      {run.fromSha || run.toSha
        ? `${run.fromSha?.slice(0, 7) || '---'}..${run.toSha?.slice(0, 7) || '---'}`
        : '—'}
    </Text>
  );
}
